import { API_BASE_URL } from '../config';
import { STORAGE_KEYS } from '../config';
import type {
  BlogPost,
  BlogPostListItem,
  BlogCategory,
  BlogTag,
  BlogMedia,
  BlogFont,
  BlogAnalyticsDashboard,
  BlogPostVersion,
} from '../types';

// Helper to get auth headers
function authHeaders(): HeadersInit {
  const token = localStorage.getItem(STORAGE_KEYS.accessToken);
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: { ...authHeaders(), ...options?.headers },
  });
  if (!res.ok) {
    const json = await res.json().catch(() => undefined);
    const message = json?.error?.message || json?.error || `${res.status}: ${res.statusText}`;
    throw new Error(message);
  }
  if (res.status === 204) return null as T;
  const body = await res.json();
  return body.data ?? body;
}

// ============================================================
// Posts
// ============================================================

export interface FetchPostsParams {
  page?: number;
  size?: number;
  status?: string;
  search?: string;
}

export interface FetchPostsResult {
  posts: BlogPostListItem[];
  total: number;
}

export async function fetchPosts(params: FetchPostsParams = {}): Promise<FetchPostsResult> {
  const query = new URLSearchParams();
  if (params.page != null) query.set('page', String(params.page));
  if (params.size != null) query.set('size', String(params.size));
  if (params.status) query.set('status', params.status);
  if (params.search) query.set('search', params.search);
  return apiFetch<FetchPostsResult>(`/backoffice/blog/posts?${query}`);
}

export async function fetchPost(id: string): Promise<BlogPost> {
  return apiFetch<BlogPost>(`/backoffice/blog/posts/${id}`);
}

export async function createPost(data: Partial<BlogPost>): Promise<BlogPost> {
  return apiFetch<BlogPost>('/backoffice/blog/posts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePost(id: string, data: Partial<BlogPost>): Promise<BlogPost> {
  return apiFetch<BlogPost>(`/backoffice/blog/posts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function autoSavePost(id: string, content: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/posts/${id}/auto-save`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
}

export async function publishPost(id: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/posts/${id}/publish`, { method: 'POST' });
}

export async function schedulePost(id: string, scheduledAt: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/posts/${id}/schedule`, {
    method: 'POST',
    body: JSON.stringify({ scheduledAt }),
  });
}

export async function submitForReview(id: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/posts/${id}/review`, { method: 'POST' });
}

export async function archivePost(id: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/posts/${id}/archive`, { method: 'POST' });
}

export async function unpublishPost(id: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/posts/${id}/unpublish`, { method: 'POST' });
}

export async function deletePost(id: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/posts/${id}`, { method: 'DELETE' });
}

// ============================================================
// Versions
// ============================================================

export async function fetchVersions(postId: string): Promise<BlogPostVersion[]> {
  return apiFetch<BlogPostVersion[]>(`/backoffice/blog/posts/${postId}/versions`);
}

export async function restoreVersion(postId: string, versionId: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/posts/${postId}/restore-version/${versionId}`, {
    method: 'POST',
  });
}

// ============================================================
// Categories
// ============================================================

export async function fetchCategories(): Promise<BlogCategory[]> {
  return apiFetch<BlogCategory[]>('/backoffice/blog/categories');
}

export async function createCategory(data: {
  name: string;
  description?: string;
  color?: string;
}): Promise<BlogCategory> {
  return apiFetch<BlogCategory>('/backoffice/blog/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCategory(id: string, data: Partial<BlogCategory>): Promise<BlogCategory> {
  return apiFetch<BlogCategory>(`/backoffice/blog/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCategory(id: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/categories/${id}`, { method: 'DELETE' });
}

// ============================================================
// Tags
// ============================================================

export async function fetchTags(): Promise<BlogTag[]> {
  return apiFetch<BlogTag[]>('/backoffice/blog/tags');
}

export async function createTag(data: { name: string }): Promise<BlogTag> {
  return apiFetch<BlogTag>('/backoffice/blog/tags', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteTag(id: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/tags/${id}`, { method: 'DELETE' });
}

// ============================================================
// Media
// ============================================================

export interface FetchMediaResult {
  media: BlogMedia[];
  items?: BlogMedia[];
  total: number;
}

export async function uploadMedia(file: File): Promise<BlogMedia> {
  const token = localStorage.getItem(STORAGE_KEYS.accessToken);
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE_URL}/backoffice/blog/media`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  if (!res.ok) {
    const json = await res.json().catch(() => undefined);
    const message = json?.error?.message || json?.error || `Upload failed: ${res.status}`;
    throw new Error(message);
  }
  const body = await res.json();
  return body.data ?? body;
}

export async function fetchMedia(page = 0, size = 20): Promise<FetchMediaResult> {
  return apiFetch<FetchMediaResult>(`/backoffice/blog/media?page=${page}&size=${size}`);
}

export async function deleteMedia(id: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/media/${id}`, { method: 'DELETE' });
}

// ============================================================
// Fonts
// ============================================================

export async function uploadFont(file: File, family: string): Promise<BlogFont> {
  const token = localStorage.getItem(STORAGE_KEYS.accessToken);
  const form = new FormData();
  form.append('file', file);
  form.append('family', family);
  const res = await fetch(`${API_BASE_URL}/backoffice/blog/fonts`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  if (!res.ok) {
    const json = await res.json().catch(() => undefined);
    const message = json?.error?.message || json?.error || `Upload failed: ${res.status}`;
    throw new Error(message);
  }
  const body = await res.json();
  return body.data ?? body;
}

export async function fetchFonts(): Promise<BlogFont[]> {
  return apiFetch<BlogFont[]>('/backoffice/blog/fonts');
}

export async function deleteFont(id: string): Promise<void> {
  return apiFetch<void>(`/backoffice/blog/fonts/${id}`, { method: 'DELETE' });
}

// ============================================================
// Analytics
// ============================================================

export async function fetchAnalyticsDashboard(from?: string, to?: string): Promise<BlogAnalyticsDashboard> {
  const query = new URLSearchParams();
  if (from) query.set('from', from);
  if (to) query.set('to', to);
  return apiFetch<BlogAnalyticsDashboard>(`/backoffice/blog/analytics/dashboard?${query}`);
}

export async function fetchPostAnalytics(
  id: string,
  from?: string,
  to?: string
): Promise<BlogAnalyticsDashboard> {
  const query = new URLSearchParams();
  if (from) query.set('from', from);
  if (to) query.set('to', to);
  return apiFetch<BlogAnalyticsDashboard>(`/backoffice/blog/analytics/posts/${id}?${query}`);
}
